import React, { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import AnimatedModal from "../../ReusableComponents/AnimatedModal";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import * as API from "../../api.js";

const CreateTowerPlan = ({
  isOpen,
  onRequestClose,
  onSubmit,
  projectData,
  editData,
}) => {
  const TOWER_TYPES = [
    { label: "Suspension", value: "Suspension" },
    { label: "Tension", value: "Tension" },
    { label: "Terminal", value: "Terminal" },
  ];

  const [formData, setFormData] = useState({
    planName: "",
    location: "",
    capacity: "",
    unit: "",
    towerType: "",
    otherType: "",
    height: "",
    span: "",
    description: "",
  });

  const [progressChecklist, setProgressChecklist] = useState({
    pits: false,
    baseplate: false,
    towerErection: false,
    stringing: false,
  });

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isUnitDropdownOpen, setIsUnitDropdownOpen] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [successModal, setSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isUpdating, setIsUpating] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChecklistChange = (e) => {
    const { name, checked } = e.target;
    setProgressChecklist((prev) => ({ ...prev, [name]: checked }));
  };

  useEffect(() => {
    if (editData && isOpen) {
      setFormData({
        planName: editData?.plan_name || "",
        location: editData?.location || "",
        capacity: editData?.capacity || "",
        unit: editData?.unit || "",
        towerType: editData?.tower_type || "",
        otherType: "",
        height: editData?.tower_height || "",
        span: editData?.tower_length || "",
        description: editData?.description || "",
      });

      // Convert progress_checklist array -> checkbox object
      const checklistObj = {
        pits: false,
        baseplate: false,
        towerErection: false,
        stringing: false,
      };
      if (Array.isArray(editData?.progress_checklist)) {
        editData.progress_checklist.forEach((key) => {
          if (checklistObj.hasOwnProperty(key)) checklistObj[key] = true;
        });
      }
      setProgressChecklist(checklistObj);
    }
  }, [editData, isOpen]);

  const handleSubmit = async () => {
    // Validate plan name
    const format = /[!@#$%^&*()+\-=\[\]{};':"\\|,.<>\/?]+/;
    if (format.test(formData.planName)) {
      setErrorMessage("Special characters are not allowed.");
      return;
    } else if (formData.planName.includes(" ")) {
      setErrorMessage("Spaces are not allowed in the plan name.");
      return;
    }

    // Convert progressChecklist object -> array
    const progressArray = Object.keys(progressChecklist).filter(
      (key) => progressChecklist[key]
    );

    // Prepare tower data object
    const towerDetails = {
      locationName: formData.location?.trim() || null,
      description: formData.description?.trim() || null,
      towerType: formData.towerType || formData.otherType || null,
      towerHeight: formData.height ? Number(formData.height) : null,
      towerLength: formData.span ? Number(formData.span) : null,
      capacity: formData.capacity ? Number(formData.capacity) : null,
      capacityUnit: formData.unit || null,
      progressChecklist: progressArray,
    };

    try {
      let response;
      if (editData && Object.keys(editData).length > 0) {
        // Edit Plan
        const editPayload = {
          ...towerDetails,
          planId: editData.id,
          planName: formData.planName.trim(),
        };
        setIsUpating(true);
        response = await API.editPlan(editPayload);
      } else {
        // Create Plan
        const Plan = {
          planConfiguration: {
            projectDetails: [
              {
                clientName: projectData?.clientName || "N/A",
                dateTime: new Date().toISOString(),
                projectName: projectData?.project_name,
                projectId: projectData?.id,
                planName: formData.planName.trim(),
                softwareVerison: "1.1",
              },
            ],
          },
          towerDetails,
          planAddress: {},
          missionStatistics: {},
          DGCAfence: [{}],
          RallyPointData: [],
          corridor: [],
          Mission: { waypoints: [{}] },
          Geofence: { geofence: [{ lat: 0, lon: 0, alt: 0 }] },
          Assets: { "planMap.png": "" },
        };
        setIsUpating(false);
        response = await API.createPlan(Plan);
      }

      if (
        response.message === "OK" ||
        response.message === "Plan updated successfully"
      ) {
        setSuccessModal(true);
        setTimeout(() => setSuccessModal(false), 2500);
        onSubmit({ ...towerDetails, planName: formData.planName.trim() });
        onRequestClose();
      } else {
        setErrorMessage(
          response.message || response.error || "Something went wrong."
        );
      }
    } catch (err) {
      console.error(err);
      setErrorMessage("Something went wrong.");
    }
  };

  return (
    <>
      {/* Success Modal */}
      <AnimatedModal
        isOpen={successModal}
        onRequestClose={() => setSuccessModal(false)}
        width="fit-content"
        height="auto"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "1.5rem",
            textAlign: "center",
          }}
        >
          <CheckCircleIcon
            style={{ fontSize: 50, color: "#4CAF50", marginBottom: "10px" }}
          />
          <h2 style={{ fontSize: "1.25rem", fontWeight: "600", color: "#333" }}>
            {isUpdating
              ? "Tower Updated Successfully!"
              : "Tower Created Successfully!"}
          </h2>
        </div>
      </AnimatedModal>

      {/* Main Modal */}
      <AnimatedModal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        width="35%"
        height="auto"
      >
        <div className="create-tower-container">
          <div className="create-tower-header">
            <p className="create-tower-title">
              {editData && Object.keys(editData).length > 0
                ? "Edit Tower"
                : "Add New Tower"}
            </p>
            <button onClick={onRequestClose} className="create-tower-close">
              ✕
            </button>
          </div>

          <div className="create-tower-form">
            {/* Tower Name */}
            <label className="create-tower-label">Tower Name</label>
            <input
              type="text"
              name="planName"
              placeholder="Enter name"
              value={formData.planName}
              onChange={(e) => {
                const value = e.target.value.replace(/[^A-Za-z0-9_]/g, "");
                setFormData({ ...formData, planName: value });
              }}
              className="create-tower-input"
            />

            {/* Location */}
            <label className="create-tower-label">Location</label>
            <input
              type="text"
              name="location"
              placeholder="Enter location"
              value={formData.location}
              onChange={handleChange}
              className="create-tower-input"
            />

            {/* Capacity & Unit */}
            <div className="create-tower-row">
              <div className="create-tower-col">
                <label className="create-tower-label">
                  Transmission Capacity
                </label>
                <input
                  type="number"
                  name="capacity"
                  placeholder="e.g. 100"
                  value={formData.capacity}
                  onChange={handleChange}
                  className="create-tower-input"
                />
              </div>
              <div className="create-tower-col">
                <label className="create-tower-label">Unit</label>
                <Dropdown
                  onSelect={(val) =>
                    setFormData((prev) => ({ ...prev, unit: val }))
                  }
                  onToggle={(isOpen) => setIsUnitDropdownOpen(isOpen)}
                >
                  <Dropdown.Toggle
                    variant="secondary"
                    bsPrefix="custom-toggle"
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      background: "#f2f2f2",
                      border: "2px solid #E5E7EB",
                      borderRadius: "5px",
                      fontSize: "12px",
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 400,
                      color: formData.unit ? "#333" : "#89889E",
                      textAlign: "start",
                      outline: "none",
                      boxShadow: "none",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>{formData.unit || "Select Unit"}</span>
                    {isUnitDropdownOpen ? (
                      <KeyboardArrowUp
                        fontSize="small"
                        style={{ color: "#89889E" }}
                      />
                    ) : (
                      <KeyboardArrowDown
                        fontSize="small"
                        style={{ color: "#89889E" }}
                      />
                    )}
                  </Dropdown.Toggle>

                  <Dropdown.Menu
                    style={{
                      backgroundColor: "#fff",
                      borderRadius: "5px",
                      width: "100%",
                      padding: 0,
                    }}
                  >
                    <Dropdown.Item
                      eventKey="KW"
                      style={{
                        fontSize: "12px",
                        color: "#222",
                        padding: "8px 10px",
                      }}
                    >
                      KW (kilowatt)
                    </Dropdown.Item>
                    <Dropdown.Item
                      eventKey="MW"
                      style={{
                        fontSize: "12px",
                        color: "#222",
                        padding: "8px 10px",
                      }}
                    >
                      MW (megawatt)
                    </Dropdown.Item>
                    <Dropdown.Item
                      eventKey="GW"
                      style={{
                        fontSize: "12px",
                        color: "#222",
                        padding: "8px 10px",
                      }}
                    >
                      GW (gigawatt)
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>

            {/* Tower Type */}
            <label className="create-tower-label">Tower Type</label>
            <Dropdown
              onSelect={(val) =>
                setFormData((prev) => ({ ...prev, towerType: val }))
              }
              onToggle={(isOpen) => setIsDropdownOpen(isOpen)}
            >
              <Dropdown.Toggle
                variant="secondary"
                bsPrefix="custom-toggle"
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  marginBottom: "14px",
                  background: "#f2f2f2",
                  border: "2px solid #E5E7EB",
                  borderRadius: "5px",
                  fontSize: "12px",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 400,
                  color: formData.towerType ? "#333" : "#89889E",
                  textAlign: "start",
                  outline: "none",
                  boxShadow: "none",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>
                  {formData.towerType
                    ? TOWER_TYPES.find((t) => t.value === formData.towerType)
                        ?.label
                    : "Select type"}
                </span>
                {isDropdownOpen ? (
                  <KeyboardArrowUp
                    fontSize="small"
                    style={{ color: "#89889E" }}
                  />
                ) : (
                  <KeyboardArrowDown
                    fontSize="small"
                    style={{ color: "#89889E" }}
                  />
                )}
              </Dropdown.Toggle>

              <Dropdown.Menu
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "5px",
                  width: "100%",
                  padding: 0,
                }}
              >
                {TOWER_TYPES.map((type) => (
                  <Dropdown.Item
                    key={type.value}
                    eventKey={type.value}
                    style={{
                      fontSize: "12px",
                      color: "#222",
                      padding: "8px 10px",
                    }}
                  >
                    {type.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <input
              type="text"
              name="otherType"
              placeholder="Other"
              value={formData.otherType}
              onChange={handleChange}
              className="create-tower-input"
            />

            {/* Height & Span */}
            <div className="create-tower-row">
              <div className="create-tower-col">
                <label className="create-tower-label">Tower Height (m)</label>
                <input
                  type="number"
                  name="height"
                  placeholder="e.g. 50m"
                  value={formData.height}
                  onChange={handleChange}
                  className="create-tower-input"
                />
              </div>
              <div className="create-tower-col">
                <label className="create-tower-label">Span Length (m)</label>
                <input
                  type="number"
                  name="span"
                  placeholder="e.g. 200m"
                  value={formData.span}
                  onChange={handleChange}
                  className="tower-length-input"
                />
              </div>
            </div>

            {/* Progress Checklist */}
            <label className="create-tower-label">Progress Checklist</label>
            <div style={{backgroundColor:'#2989CF1A',width:"56%"}} className="inline-block bg-blue-50 text-gray-800 text-sm px-2 py-2 rounded-md">
      Installation is <span style={{color:"#2989CF",fontSize:"small"}}>{Object.values(progressChecklist).every(Boolean)
  ? "Completed"
  : Object.values(progressChecklist).some(Boolean)
  ? "In Progress"
  : "Pending"} </span>
    </div>
            <div style={{ marginBottom: "20px" }}>
              {/* First Row */}
              <div
                style={{
                  display: "flex",
                  gap: "40px",
                  marginBottom: "20px",
                  marginTop: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                    minWidth: "120px",
                  }}
                >
                  <label
                    htmlFor="pits"
                    className="create-tower-label"
                    style={{ lineHeight: "18px", margin: 0 }}
                  >
                    Pits
                  </label>
                  <input
                    type="checkbox"
                    name="pits"
                    checked={progressChecklist.pits}
                    onChange={handleChecklistChange}
                    id="pits"
                    style={{
                      width: "18px",
                      height: "18px",
                      margin: 0,
                      marginLeft: "80px",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                    minWidth: "120px",
                  }}
                >
                  <label
                    htmlFor="baseplate"
                    className="create-tower-label"
                    style={{ lineHeight: "18px", marginLeft: "10px" }}
                  >
                    Base Plate
                  </label>
                  <input
                    type="checkbox"
                    name="baseplate"
                    checked={progressChecklist.baseplate}
                    onChange={handleChecklistChange}
                    id="baseplate"
                    style={{
                      width: "18px",
                      height: "18px",
                      margin: 0,
                      marginLeft: "80px",
                    }}
                  />
                </div>
              </div>

              {/* Second Row */}
              <div style={{ display: "flex", gap: "40px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                    minWidth: "120px",
                  }}
                >
                  <label
                    htmlFor="towerErection"
                    style={{ lineHeight: "18px", margin: 0 }}
                    className="create-tower-label"
                  >
                    Tower Erection
                  </label>
                  <input
                    type="checkbox"
                    name="towerErection"
                    checked={progressChecklist.towerErection}
                    onChange={handleChecklistChange}
                    id="towerErection"
                    style={{
                      width: "18px",
                      height: "18px",
                      margin: 0,
                      marginLeft: "21px",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                    minWidth: "120px",
                  }}
                >
                  <label
                    htmlFor="stringing"
                    style={{ lineHeight: "18px", marginLeft: "9px" }}
                    className="create-tower-label"
                  >
                    Stringing
                  </label>
                  <input
                    type="checkbox"
                    name="stringing"
                    checked={progressChecklist.stringing}
                    onChange={handleChecklistChange}
                    id="stringing"
                    style={{
                      width: "18px",
                      height: "18px",
                      marginLeft: "90px",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <label className="create-tower-label">Description</label>
            <textarea
              name="description"
              placeholder="Enter description"
              value={formData.description}
              onChange={handleChange}
              className="create-tower-input"
            />
          </div>

          <div className="create-tower-footer">
            <button onClick={handleSubmit} className="create-tower-submit">
              {editData && Object.keys(editData).length > 0 ? "Update" : "Add"}
            </button>
          </div>
        </div>
      </AnimatedModal>
    </>
  );
};

export default CreateTowerPlan;
